import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowRight, CircleAlert, LoaderCircle } from 'lucide-react';
import InteractiveDotGrid from '../components/shared/InteractiveDotGrid';
import {
  isAuthenticated,
  getDefaultRedirectPathByUserType,
  AUTH_SESSION_TOKENS_UPDATED_EVENT,
  appendForceLogin,
  beginLogin,
  requiresFreshLogin,
  discardLeftoverSession,
  endIdpSessionThen,
  consumePendingAuthorize,
} from '../services/api';
import '../styles/pages/LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { loading, error, clearError } = useApp();
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    beginLogin();
    discardLeftoverSession();
    clearError();

    const pending = consumePendingAuthorize();
    if (pending && pending.includes('/oauth2/authorize')) {
      window.location.assign(pending);
    }
  }, [clearError]);

  useEffect(() => {
    const tryReturnToApp = () => {
      if (requiresFreshLogin()) {
        discardLeftoverSession();
        return;
      }
      if (!isAuthenticated()) return;
      const from = state?.from;
      const fromPath = from?.pathname;
      const target =
        fromPath && fromPath !== '/login'
          ? `${fromPath}${from.search ?? ''}${from.hash ?? ''}`
          : getDefaultRedirectPathByUserType();
      navigate(target, { replace: true });
    };

    tryReturnToApp();
    window.addEventListener(AUTH_SESSION_TOKENS_UPDATED_EVENT, tryReturnToApp);
    return () =>
      window.removeEventListener(AUTH_SESSION_TOKENS_UPDATED_EVENT, tryReturnToApp);
  }, [navigate, state]);

  const handleLogin = () => {
    beginLogin();
    discardLeftoverSession();
    clearError();
    setStarting(true);
    try {
      const from = state?.from;
      const fromPath = from?.pathname;
      if (fromPath && fromPath !== '/login') {
        localStorage.setItem(
          'postLoginRedirect',
          `${fromPath}${from.search ?? ''}${from.hash ?? ''}`
        );
      }

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: import.meta.env.VITE_CLIENT_ID,
        redirect_uri: import.meta.env.VITE_REDIRECT_URI,
        scope: 'Support:User Support:Admin users:read users:write',
        state: JSON.stringify({ ...state })
      });

      appendForceLogin(params);
      params.set('prompt', 'login');
      params.set('max_age', '0');

      const authUrl = `${import.meta.env.VITE_AUTH_URL}/authorize?${params.toString()}`;
      endIdpSessionThen(authUrl);
    } catch (err) {
      console.error('Error during login:', err);
      setStarting(false);
    }
  };

  const busy = loading || starting;

  return (
    <div className="login-page">
      <aside className="login-hero" aria-hidden="true">
        <InteractiveDotGrid />

        <div className="login-hero__brand">
          <img src="/supporthub-logo.png" alt="" className="login-hero__logo-sm" />
          <span>SupportHub</span>
        </div>

        <div className="login-hero__content">
          <h2>Todos os seus tickets em um só lugar.</h2>
          <p>Abra, acompanhe e resolva demandas de suporte, melhorias e projetos.</p>
        </div>

        <span className="login-hero__footer">© {new Date().getFullYear()} Elevate</span>
      </aside>

      <main className="login-main">
        <div className="login-card">
          <img src="/supporthub-logo.png" alt="SupportHub" className="login-card__logo" />
          <h1>Entrar no SupportHub</h1>
          <p className="login-card__subtitle">
            Você será redirecionado para a página de autenticação segura.
          </p>

          {error && error !== 'Session ending' && (
            <div className="error-message" role="alert">
              <CircleAlert size={18} strokeWidth={2} aria-hidden />
              <span>{error}</span>
            </div>
          )}

          <button
            type="button"
            className="login-button"
            onClick={handleLogin}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? (
              <>
                <LoaderCircle size={18} strokeWidth={2} className="login-spin" aria-hidden />
                <span>{starting ? 'Encerrando a sessão anterior...' : 'Entrando...'}</span>
              </>
            ) : (
              <>
                <span>Entrar</span>
                <ArrowRight size={18} strokeWidth={2} className="login-button__arrow" aria-hidden />
              </>
            )}
          </button>

          <p className="login-card__help">
            Problemas para entrar? Fale com o administrador do sistema.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;